import { useQuery } from '@tanstack/react-query';
import {
  PRODUCTION_FORMAT_LABELS,
  PROJECT_DEFINITION,
  projectSlug,
} from '@scoutaround/script-breakdown-shared';

import { api, isWorkspaceRoleRequired, type Project } from '../lib/api.js';
import { Icon, type IconName } from '../components/Icon.js';
import projectFlow from '../assets/project-flow.webp';
import { HealthTag, LifecycleTag } from '../components/StatusTag.js';
import { WorkspaceAccessRequired } from '../components/WorkspaceAccessRequired.js';

/** The four steps, in the order the work happens. */
const FLOW: readonly { label: string; icon: IconName }[] = [
  { label: 'Script & brief', icon: 'script' },
  { label: 'Location needs', icon: 'pin' },
  { label: 'Scouting', icon: 'scout' },
  { label: 'Shortlist', icon: 'shortlist' },
];

/** Promises, not a feature list — what a producer gets, not what we built. */
const PROMISES: readonly { label: string; icon: IconName }[] = [
  { label: 'Keep everything in one place', icon: 'bolt' },
  { label: 'Work with your team', icon: 'team' },
  { label: 'Find and compare locations faster', icon: 'map' },
];

const MODEL_LABEL: Record<string, string> = {
  TRADITIONAL: 'Traditional',
  HYBRID: 'Hybrid',
  AI: 'AI',
};

function initials(title: string): string {
  return title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function ProjectsPage({
  onOpen,
  onContinueSetup,
  onNewProject,
  onNewSearch,
}: {
  onOpen: (address: string) => void;
  /** Into the guided setup, at whatever step the production is on. */
  onContinueSetup: (address: string) => void;
  onNewProject: () => void;
  onNewSearch: () => void;
}) {
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.listProjects });
  // Unread team conversations per production (ADR 0021). The server answers an
  // empty list while the platform has conversations switched off.
  const unread = useQuery({
    queryKey: ['conversations-unread'],
    queryFn: api.unreadConversations,
    refetchInterval: 60_000,
    retry: false,
  });
  const unreadFor = (projectId: string) => unread.data?.projects.find((u) => u.projectId === projectId);

  // Undefined while loading — neither state is committed to until the answer
  // is in, or the header flickers its buttons on every visit.
  const hasProjects = projects.data !== undefined && projects.data.length > 0;

  return (
    <div data-testid="projects-page">
      <section style={{ borderBottom: '2px solid var(--color-divider)', background: 'var(--color-surface)' }}>
        <div style={{ maxWidth: 1360, margin: '0 auto', padding: '38px 40px 30px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <p className="kick">Production workspace</p>
              <h1 className="dh" style={{ fontSize: 52, marginTop: 10 }}>
                Projects
              </h1>
              <p className="mm" style={{ fontSize: 14.5, lineHeight: 1.6, margin: '10px 0 0', maxWidth: '62ch' }}>
                Keep every production organized in one place — from script and location
                requirements to scouting, shortlists and final decisions.
              </p>
            </div>
            {/* NOTHING HERE UNTIL THERE IS SOMETHING TO ADD TO.
                A first-time producer met two buttons offering the same thing —
                one in the header, one in the empty state — and a choice
                between duplicates is a choice about nothing. At zero the
                centre of the screen leads and there is a single action on the
                page; from the first project onward this is a workspace and the
                header carries the way to add another. */}
            {hasProjects ? (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="new-project"
                  onClick={() => {
                    onNewProject();
                  }}
                >
                  + New project
                </button>
                {/* The search creates its own project when there is not one
                    yet, so it is a shortcut INTO the model rather than a
                    second door — which is exactly why it has no place at zero,
                    where the model is what is being explained. */}
                <button
                  type="button"
                  className="btn btn-ghost"
                  data-testid="new-location-search"
                  onClick={onNewSearch}
                >
                  New location search
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section style={{ maxWidth: 1360, margin: '0 auto', padding: '26px 40px 64px' }}>
        {projects.isPending ? <p className="mm">Loading projects…</p> : null}
        {projects.isError && isWorkspaceRoleRequired(projects.error) ? (
          // NOT AN ERROR LINE. The account is signed in and nothing failed:
          // ScoutAround has not enabled the workspace for it, and a red
          // sentence with no way on read as a fault to retry.
          <div className="card1" style={{ padding: 32, maxWidth: 640 }}>
            <WorkspaceAccessRequired
              testId="projects-no-access"
              heading="Production workspace not enabled"
              message={projects.error.message}
            />
          </div>
        ) : projects.isError ? (
          <p data-testid="projects-error" style={{ color: 'var(--danger)' }}>
            {projects.error.message}
          </p>
        ) : null}

        {projects.data && projects.data.length === 0 ? (
          /**
           * THE FIRST VISIT IS ONBOARDING; EVERY LATER ONE IS A WORKSPACE.
           *
           * This said "No projects yet" and then described a project as a thing
           * that HOLDS a production — a database row, not a promise. It told
           * somebody what the record contains and nothing about why they would
           * want one.
           *
           * The chain is the message: production → project → requirements →
           * locations → decisions, in three or four seconds. A producer who
           * reads "project" as "folder" will look for their files in it and
           * find a workspace they did not know they had.
           */
          <div
            className="card1"
            style={{ padding: '44px 32px 0', textAlign: 'center', overflow: 'hidden' }}
            data-testid="projects-empty"
          >
            {/* DECORATIVE, so `alt` is empty: it draws the same four steps the
                chips below spell out, and a screen reader repeating them is
                noise. Sized in CSS rather than fixed, so it scales on a phone
                instead of forcing a horizontal scroll. */}
            <img
              src={projectFlow}
              alt=""
              data-testid="projects-empty-visual"
              style={{ width: '100%', maxWidth: 620, height: 'auto', display: 'block', margin: '0 auto' }}
            />

            <p className="dh" style={{ fontSize: 'clamp(24px, 3.4vw, 32px)', margin: '18px 0 0' }}>
              Your production starts here
            </p>
            <p
              className="mm"
              style={{ fontSize: 14.5, lineHeight: 1.65, margin: '12px auto 0', maxWidth: '56ch' }}
            >
              Create a project to bring your script, location requirements, scouting and
              shortlists into one connected workspace.
            </p>

            {/* The order IS the message, so it is an ordered list — a screen
                reader gets the sequence, not four loose words. The arrows are
                decorative and hidden from it. */}
            <ol
              data-testid="project-flow"
              style={{
                listStyle: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 10,
                margin: '26px 0 0',
                padding: 0,
              }}
            >
              {FLOW.map((step, i) => (
                <li key={step.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {i === 0 ? null : (
                    <span aria-hidden="true" className="mm" style={{ fontSize: 15 }}>
                      →
                    </span>
                  )}
                  <span
                    className="flbl"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 9,
                      letterSpacing: '0.08em',
                      padding: '11px 15px',
                      border: '1px solid var(--color-divider)',
                      background: 'var(--color-bg)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Icon name={step.icon} />
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>

            <p className="mm" data-testid="project-definition" style={{ fontSize: 12.5, margin: '20px 0 0' }}>
              {PROJECT_DEFINITION}
            </p>

            {/* ONE ACTION ON THE PAGE. No secondary link beside it: a choice at
                the moment somebody is still working out what this is costs more
                than it offers. */}
            <button
              type="button"
              className="btn btn-primary"
              data-testid="create-first-project"
              onClick={onNewProject}
              style={{ marginTop: 22, fontSize: 14 }}
            >
              Create your first project
            </button>

            {/* Three promises rather than three features, and they sit BELOW the
                action — somebody already convinced should not have to read past
                them to reach the button. */}
            <ul
              data-testid="projects-empty-promises"
              style={{
                listStyle: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '10px 40px',
                margin: '34px -32px 0',
                padding: '18px 32px',
                borderTop: '1px solid var(--color-divider)',
              }}
            >
              {PROMISES.map((promise) => (
                <li
                  key={promise.label}
                  className="mm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}
                >
                  <Icon name={promise.icon} />
                  {promise.label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {projects.data && projects.data.length > 0 ? (
          <>
            <div
              className="prow prow-head"
              style={{
                gap: 14,
                padding: '14px 18px 10px',
                borderBottom: '2px solid var(--ink)',
              }}
            >
              <span className="colh">Project</span>
              <span className="colh">Phase</span>
              <span className="colh">Health</span>
              <span className="colh">Model</span>
              <span className="colh" aria-hidden="true" />
            </div>

            {projects.data.map((p: Project) => {
              // The readable address, so the first URL a producer ever sees
              // for a production is the one worth sharing — not a ULID they
              // then copy into a message.
              const address = p.urlKey === null ? p.id : projectSlug(p.title, p.urlKey);

              return (
              /* The whole row opens the production — through the title button,
                 stretched over the row by CSS, so it stays a real button and
                 the row can still hold a second action beside it. A button
                 inside a button is not HTML. */
              <div
                key={p.id}
                data-testid={`project-row-${p.id}`}
                className="prow"
                style={{
                  gap: 14,
                  padding: '14px 18px',
                  marginTop: -1,
                  background: 'var(--color-bg)',
                  boxShadow: 'inset 0 0 0 1px var(--color-divider)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                  <span
                    style={{
                      width: 44,
                      height: 58,
                      flex: 'none',
                      background: 'var(--color-neutral-100)',
                      color: 'var(--ink)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      fontSize: 17,
                      border: '1px solid var(--color-divider)',
                    }}
                  >
                    {initials(p.title)}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <button
                      type="button"
                      className="dh prow-open"
                      data-testid={`project-open-${p.id}`}
                      style={{ fontSize: 21, display: 'block' }}
                      onClick={() => {
                        onOpen(address);
                      }}
                    >
                      {p.title}
                    </button>
                    <span className="mm" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      {PRODUCTION_FORMAT_LABELS[p.format] ?? p.format}
                      {unreadFor(p.id) ? (
                        <span
                          data-testid={`project-conversations-unread-${p.id}`}
                          style={{ marginLeft: 10, color: 'var(--color-accent-700)', fontWeight: 700 }}
                        >
                          {`${String(unreadFor(p.id)?.unread ?? 0)} unread in conversations${(unreadFor(p.id)?.mentions ?? 0) > 0 ? ' · you were mentioned' : ''}`}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </span>
                <span>
                  <LifecycleTag phase={p.lifecyclePhase} />
                </span>
                <span>
                  <HealthTag derived={p.derivedHealth} override={p.healthOverride} />
                </span>
                <span className="mm" style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {MODEL_LABEL[p.productionModel] ?? p.productionModel}
                </span>
                {/* Offered while the first pass is unfinished; a finished
                    production shows nothing extra. Where it lands is the
                    server's `nextStep`, resolved on the way in. */}
                <span className="prow-action">
                  {p.setupCompletedAt === null ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      data-testid={`project-continue-setup-${p.id}`}
                      onClick={() => {
                        onContinueSetup(address);
                      }}
                    >
                      Continue setup
                    </button>
                  ) : null}
                </span>
              </div>
              );
            })}
          </>
        ) : null}
      </section>
    </div>
  );
}
